/* Isolated homepage enhancement. Existing PDP/cart review code is untouched. */
(() => {
  'use strict';
  if (customElements.get('homepage-review-summary')) return;
  const summaries = new Map();
  async function widget(element, endpoint, extra = {}) {
    const params = new URLSearchParams({shop_domain:element.dataset.shopDomain,api_token:element.dataset.apiToken,json_request:'true',...extra});
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch('https://judge.me/api/v1/widgets/'+endpoint+'?'+params,{signal:controller.signal});
      if (!response.ok) throw new Error('Review provider unavailable');
      return await response.json();
    } finally { clearTimeout(timer); }
  }
  class HomepageReviewSummary extends HTMLElement {
    connectedCallback() {
      if (this.started || !this.dataset.apiToken || !this.dataset.shopDomain) return;
      this.started = true;
      const key = this.dataset.shopDomain+':'+this.dataset.apiToken;
      if (!summaries.has(key)) summaries.set(key,Promise.all([widget(this,'shop_reviews_count'),widget(this,'shop_reviews_rating')]).catch(error=>{summaries.delete(key);throw error;}));
      summaries.get(key).then(([countPayload,ratingPayload])=>{
        const count = Number(countPayload.shop_reviews_count);
        const rating = Number(ratingPayload.shop_reviews_rating);
        if (!this.isConnected || ratingPayload.shop_reviews_rating==null || ratingPayload.shop_reviews_rating==='' || !Number.isInteger(count) || count<=0 || !Number.isFinite(rating) || rating<1 || rating>5) return;
        const locale = this.dataset.locale || 'en';
        const score = new Intl.NumberFormat(locale,{minimumFractionDigits:1,maximumFractionDigits:1}).format(rating);
        const category = new Intl.PluralRules(locale).select(count);
        const template = category==='one'?this.dataset.countOne:category==='few'?this.dataset.countFew:this.dataset.countOther;
        this.querySelector('[data-rating-score]').textContent = score;
        this.querySelector('[data-rating-count]').textContent = template.replace('__COUNT__',String(count));
        this.querySelector('[data-rating-stars]').style.setProperty('--homepage-rating-fill',String(Math.round(rating*20*100)/100)+'%');
        this.querySelector('a').setAttribute('aria-label',this.dataset.ratingLabel.replace('__RATING__',score).replace('__COUNT__',String(count)));
        this.querySelector('[data-rating-fallback]').hidden = true;
        this.querySelector('[data-rating-values]').hidden = false;
        this.querySelector('[data-rating-count]').hidden = false;
      }).catch(()=>{/* The original, usable customer-experience link remains. */});
    }
  }
  class HomepageStoreReviews extends HTMLElement {
    connectedCallback() {
      if (this.started || !this.dataset.apiToken || !this.dataset.shopDomain) return;
      this.started = true;
      this.details = this.querySelector('[data-full-reviews]');
      this.toggle = this.querySelector('[data-toggle-reviews]');
      this.feed = this.querySelector('[data-review-feed]');
      this.status = this.querySelector('[data-reviews-status]');
      this.retry = this.querySelector('[data-reviews-retry]');
      this.more = this.querySelector('[data-reviews-more]');
      if (!this.details || !this.feed || !this.status || !this.retry || !this.more || !this.toggle) return;
      this.currentPage = 0;
      this.totalPages = 1;
      this.seen = new Set();
      this.details.hidden = false;
      this.toggle.hidden = false;
      this.details.addEventListener('toggle',()=>{
        this.toggle.setAttribute('aria-expanded',String(this.details.open));
        if (this.details.open && !this.loaded && !this.loading) this.loadPage(1);
      });
      this.toggle.addEventListener('click',()=>{this.details.open = !this.details.open;});
      this.retry.addEventListener('click',()=>this.loadPage(this.failedPage || 1,{reveal:this.failedPage>1}));
      this.more.addEventListener('click',()=>{
        if (!this.loading && this.currentPage<this.totalPages) this.loadPage(this.currentPage+1,{reveal:true});
      });
    }
    async loadPage(page,{reveal=false}={}) {
      if (this.loading) return;
      this.loading = true;
      this.status.textContent = this.dataset.loadingLabel;
      this.feed.setAttribute('aria-busy','true');
      this.retry.hidden = true;
      this.more.hidden = true;
      try {
        const payload = await widget(this,'all_reviews_page',{review_type:'shop-reviews',per_page:'20',page:String(page)});
        if (!this.isConnected) return;
        if (!Array.isArray(payload.reviews)) throw new Error('Invalid review response');
        let firstAdded = null;
        for (const review of payload.reviews) {
          if (review.is_shop_review!==true || typeof review.body!=='string' || !review.body.trim() || !Number.isInteger(review.rating) || review.rating<1 || review.rating>5) continue;
          const key = review.uuid || review.reviewer_name+'\n'+review.body;
          if (this.seen.has(key)) continue;
          this.seen.add(key);
          const item = document.createElement('li');
          const stars = document.createElement('span');
          stars.className = 'homepage-reviews__stars';
          stars.textContent = '★'.repeat(review.rating);
          stars.setAttribute('role','img');
          stars.setAttribute('aria-label',this.dataset.starsLabel.replace('__RATING__',String(review.rating)));
          const quote = document.createElement('blockquote');
          quote.textContent = review.body;
          if (typeof review.language==='string' && /^[a-z]{2}(?:-[A-Za-z]{2})?$/.test(review.language)) quote.lang = review.language;
          const author = document.createElement('p');
          author.className = 'homepage-reviews__author';
          author.textContent = typeof review.reviewer_name==='string'?review.reviewer_name:'';
          const createdAt = typeof review.created_at==='string' ? new Date(review.created_at) : null;
          if (createdAt && !Number.isNaN(createdAt.getTime())) {
            const time = document.createElement('time');
            time.dateTime = createdAt.toISOString();
            time.textContent = new Intl.DateTimeFormat(this.dataset.locale || 'en', {
              day:'numeric', month:'long', year:'numeric',
            }).format(createdAt);
            if (author.textContent) author.append(' · ');
            author.append(time);
          }
          item.append(stars,quote,author);
          this.feed.append(item);
          if (!firstAdded) firstAdded = item;
        }
        this.currentPage = page;
        this.totalPages = Number.isInteger(payload.total_pages) && payload.total_pages>=page ? payload.total_pages : page;
        this.loaded = true;
        this.failedPage = null;
        this.status.textContent = this.feed.children.length ? '' : this.dataset.emptyLabel;
        this.more.hidden = this.currentPage>=this.totalPages;
        if (reveal && firstAdded) {
          firstAdded.tabIndex = -1;
          firstAdded.focus({preventScroll:true});
          firstAdded.scrollIntoView({block:'center',inline:'nearest',behavior:'auto'});
        }
      } catch (_) {
        if (!this.isConnected) return;
        this.loaded = true;
        this.failedPage = page;
        this.status.textContent = this.dataset.errorLabel;
        this.retry.hidden = false;
      } finally {
        this.loading = false;
        this.feed.setAttribute('aria-busy','false');
      }
    }
  }
  customElements.define('homepage-review-summary',HomepageReviewSummary);
  customElements.define('homepage-store-reviews',HomepageStoreReviews);
})();
